"use client";

import { useActionState, useState } from "react";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { registerAction, type RegisterState } from "../actions";
import { t } from "@/lib/i18n";
import { useLang } from "../../LangProvider";
import FloatingControls from "../../FloatingControls";
import PasswordInput from "@/components/PasswordInput";

const initial: RegisterState = {};

const inputCls =
  "w-full rounded-lg border border-[var(--p-input-border)] bg-[var(--p-input-bg)] px-3 py-2 text-sm text-[var(--p-input-text)] placeholder:text-[var(--p-input-ph)] outline-none focus:border-[var(--p-heading)]";

export default function RegisterPage() {
  const lang = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [signingIn, setSigningIn] = useState(false);

  const [state, formAction, pending] = useActionState(
    async (prev: RegisterState, formData: FormData) => {
      const res = await registerAction(prev, formData);
      if (res.ok) {
        setSigningIn(true);
        await signIn("credentials", {
          email,
          password,
          callbackUrl: "/account",
        });
      }
      return res;
    },
    initial,
  );

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <FloatingControls />
      <form
        action={formAction}
        className="w-full max-w-sm rounded-2xl border border-[var(--p-card-border)] bg-[var(--p-card)] p-8 shadow-xl backdrop-blur"
      >
        <h1 className="mb-1 text-2xl font-bold text-[var(--p-heading)]">
          {t(lang, "register.title")}
        </h1>
        <p className="mb-6 text-sm text-[var(--p-muted)]">
          {t(lang, "register.subtitle")}
        </p>

        <input
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t(lang, "register.name")}
          className={`mb-3 ${inputCls}`}
        />
        <input
          name="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t(lang, "register.email")}
          className={`mb-3 ${inputCls}`}
        />
        <label className="mb-3 flex items-center justify-between gap-3 text-sm text-[var(--p-muted)]">
          <span>{t(lang, "register.children_count")}</span>
          <select name="childrenCount" defaultValue="1" className={`${inputCls} max-w-[5.5rem]`}>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <PasswordInput
          name="password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t(lang, "register.password")}
          className={`mb-4 ${inputCls}`}
        />

        {state.error && (
          <p className="mb-4 text-sm text-red-500">{state.error}</p>
        )}

        <button
          type="submit"
          disabled={pending || signingIn}
          className="w-full rounded-full bg-[#4B3FA0] py-3 font-medium text-white transition hover:bg-[#3d3384] disabled:opacity-60"
        >
          {pending || signingIn
            ? t(lang, "register.submitting")
            : t(lang, "register.submit")}
        </button>

        <p className="mt-5 text-center text-sm text-[var(--p-muted)]">
          {t(lang, "register.has_account")}{" "}
          <Link href="/login" className="text-[var(--p-heading)] underline">
            {t(lang, "register.login")}
          </Link>
        </p>
      </form>
    </main>
  );
}
